// Procedural audio: SFX + ambient music (sin assets externos)
// Web Audio API. AudioContext se desbloquea en el primer gesto del usuario.

let _ctx = null;
let _muted = JSON.parse(localStorage.getItem('letris.muted') || 'false');
let _musicMuted = JSON.parse(localStorage.getItem('letris.musicMuted') || 'false');
let _master = null;
let _musicGain = null;
let _sfxGain = null;
let _musicNodes = [];

function ctx() {
  if (!_ctx) {
    const C = window.AudioContext || window.webkitAudioContext;
    _ctx = new C();
    _master = _ctx.createGain();
    _master.gain.value = 1;
    _master.connect(_ctx.destination);
    _sfxGain = _ctx.createGain();
    _sfxGain.gain.value = _muted ? 0 : 0.7;
    _sfxGain.connect(_master);
    _musicGain = _ctx.createGain();
    _musicGain.gain.value = _musicMuted || _muted ? 0 : 0.13;
    _musicGain.connect(_master);
  }
  return _ctx;
}

// Desbloquea contexto en cualquier interacción del usuario (browser autoplay policy).
function unlock() {
  const c = ctx();
  if (c.state === 'suspended') c.resume();
}
window.addEventListener('pointerdown', unlock, { once: false, passive: true });
window.addEventListener('keydown', unlock, { once: false, passive: true });

export function isMuted() { return _muted; }
export function isMusicMuted() { return _musicMuted; }
export function setMuted(v) {
  _muted = !!v;
  localStorage.setItem('letris.muted', JSON.stringify(_muted));
  if (_sfxGain) _sfxGain.gain.value = _muted ? 0 : 0.7;
  if (_musicGain) _musicGain.gain.value = (_musicMuted || _muted) ? 0 : 0.13;
}
export function setMusicMuted(v) {
  _musicMuted = !!v;
  localStorage.setItem('letris.musicMuted', JSON.stringify(_musicMuted));
  if (_musicGain) _musicGain.gain.value = (_musicMuted || _muted) ? 0 : 0.13;
}

function tone({ freq, dur, type = 'sine', vol = 0.2, slide, attack = 0.005, release = null, when = 0 }) {
  if (_muted) return;
  const c = ctx();
  const t0 = c.currentTime + when;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (slide) osc.frequency.exponentialRampToValueAtTime(Math.max(20, slide), t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(vol, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g);
  g.connect(_sfxGain);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

function chord(freqs, dur, type = 'triangle', vol = 0.15, when = 0) {
  for (const f of freqs) tone({ freq: f, dur, type, vol, when });
}

function noiseBurst({ dur = 0.15, vol = 0.15, lowpass = 2000, highpass = 0, when = 0 }) {
  if (_muted) return;
  const c = ctx();
  const t0 = c.currentTime + when;
  const buf = c.createBuffer(1, Math.max(1, Math.floor(c.sampleRate * dur)), c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = lowpass;
  let last = filter;
  if (highpass > 0) {
    const hp = c.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = highpass;
    last.connect(hp);
    last = hp;
  }
  const g = c.createGain();
  g.gain.setValueAtTime(vol, t0);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  src.connect(filter);
  last.connect(g);
  g.connect(_sfxGain);
  src.start(t0);
  src.stop(t0 + dur + 0.05);
}

// ====================== SFX BANK ======================
export const SFX = {
  click: () => tone({ freq: 620, dur: 0.06, type: 'square', vol: 0.08 }),
  reject: () => { tone({ freq: 200, dur: 0.10, type: 'square', vol: 0.13, slide: 130 }); },

  tileLand: () => tone({ freq: 320, dur: 0.10, type: 'sine', vol: 0.13, slide: 200 }),
  tileSlam: () => {
    tone({ freq: 110, dur: 0.18, type: 'sawtooth', vol: 0.20, slide: 55 });
    noiseBurst({ dur: 0.12, vol: 0.10, lowpass: 800 });
  },

  merge: (level = 0) => {
    // Arpegio mayor ascendente con sparkle: pop satisfactorio
    const base = 392 * Math.pow(1.12, level);
    const ratios = [1, 1.25, 1.5, 2];   // Maj triad + octava
    ratios.forEach((r, i) => tone({
      freq: base * r,
      dur: 0.22 - i * 0.02,
      type: 'triangle',
      vol: 0.18 - i * 0.015,
      when: i * 0.035,
    }));
    // Sparkle sub-tone (octava arriba, square corta)
    tone({ freq: base * 3, dur: 0.10, type: 'square', vol: 0.06, when: 0.12 });
    // Shimmer noise top
    noiseBurst({ dur: 0.10, vol: 0.04, lowpass: 9000, highpass: 4500, when: 0.04 });
    // Body thump grave
    tone({ freq: base * 0.5, dur: 0.18, type: 'sine', vol: 0.12, when: 0, slide: base * 0.45 });
  },

  push: () => tone({ freq: 280, dur: 0.06, type: 'square', vol: 0.07, slide: 220 }),

  wordValid: (len = 3) => {
    const base = 440;
    const steps = Math.min(len, 7);
    for (let i = 0; i < steps; i++) {
      tone({ freq: base * Math.pow(1.122, i * 1.5), dur: 0.13, type: 'triangle', vol: 0.16, when: i * 0.06 });
    }
  },
  wordInvalid: () => {
    tone({ freq: 240, dur: 0.18, type: 'sawtooth', vol: 0.18, slide: 100 });
    noiseBurst({ dur: 0.10, vol: 0.06, lowpass: 600 });
  },

  combo: (n = 2) => {
    const base = [523, 659, 784, 988, 1175];
    for (let i = 0; i < Math.min(n, 5); i++) {
      tone({ freq: base[i] || 1175, dur: 0.14, type: 'triangle', vol: 0.16, when: i * 0.07 });
    }
  },

  levelUp: () => {
    const notes = [523, 659, 784, 1046, 1318];
    notes.forEach((f, i) => tone({ freq: f, dur: 0.22, type: 'triangle', vol: 0.18, when: i * 0.085 }));
    noiseBurst({ dur: 0.4, vol: 0.05, lowpass: 4000, highpass: 1000, when: 0.1 });
  },

  coinGain: () => {
    tone({ freq: 880, dur: 0.05, type: 'square', vol: 0.10 });
    tone({ freq: 1320, dur: 0.08, type: 'square', vol: 0.10, when: 0.05 });
  },

  bombTick: () => tone({ freq: 950, dur: 0.04, type: 'square', vol: 0.07 }),
  bombExplode: () => {
    tone({ freq: 70, dur: 0.32, type: 'sawtooth', vol: 0.28, slide: 30 });
    noiseBurst({ dur: 0.45, vol: 0.22, lowpass: 700 });
    noiseBurst({ dur: 0.18, vol: 0.10, lowpass: 3500, highpass: 600, when: 0.04 });
  },

  bonus: () => chord([523, 784, 1046], 0.28, 'triangle', 0.16),

  iceFreeze: () => tone({ freq: 300, dur: 0.22, type: 'sine', vol: 0.13, slide: 900 }),
  iceThaw: () => {
    tone({ freq: 900, dur: 0.18, type: 'sine', vol: 0.13, slide: 380 });
    noiseBurst({ dur: 0.14, vol: 0.05, lowpass: 5000, highpass: 2000, when: 0.05 });
  },

  mysterySpawn: () => {
    [523, 659, 784, 1046].forEach((f, i) =>
      tone({ freq: f, dur: 0.10, type: 'sine', vol: 0.12, when: i * 0.05 }));
  },
  slotSpin: () => tone({ freq: 600, dur: 0.05, type: 'square', vol: 0.07 }),
  slotStop: () => {
    chord([523, 659, 784], 0.4, 'triangle', 0.16);
  },

  hint: () => {
    tone({ freq: 700, dur: 0.07, type: 'triangle', vol: 0.07 });
    tone({ freq: 1050, dur: 0.07, type: 'triangle', vol: 0.06, when: 0.06 });
  },

  letrisDrop: () => {
    [523, 698, 880, 1046].forEach((f, i) =>
      tone({ freq: f, dur: 0.18, type: 'triangle', vol: 0.18, when: i * 0.08 }));
  },
  letrisEquip: () => chord([392, 523, 659, 784], 0.32, 'triangle', 0.18),
  letrisDiscard: () => tone({ freq: 220, dur: 0.18, type: 'sawtooth', vol: 0.13, slide: 130 }),
};

// ====================== MUSIC (sequencer procedural con drums + bass + pad + lead) ======================
const NOTE_FREQ = {
  'C': 261.63, 'C#': 277.18, 'D': 293.66, 'D#': 311.13, 'E': 329.63,
  'F': 349.23, 'F#': 369.99, 'G': 392.00, 'G#': 415.30, 'A': 440.00,
  'A#': 466.16, 'B': 493.88,
};
function freqOf(note, oct = 0) { return NOTE_FREQ[note] * Math.pow(2, oct); }

// Progresión Am - F - C - G (Vi-IV-I-V en relativo, lofi/chill clásica)
const CHORD_PROG = [
  { name: 'Am', notes: ['A', 'C', 'E'], root: 'A' },
  { name: 'F',  notes: ['F', 'A', 'C'], root: 'F' },
  { name: 'C',  notes: ['C', 'E', 'G'], root: 'C' },
  { name: 'G',  notes: ['G', 'B', 'D'], root: 'G' },
];
const PENTATONIC_A_MIN = ['A', 'C', 'D', 'E', 'G'];

const BPM = 84;
const SEC_PER_BEAT = 60 / BPM;
const SEC_PER_16TH = SEC_PER_BEAT / 4;
const STEPS_PER_BAR = 16;

let _seqStep = 0;
let _seqNextTime = 0;
let _seqTimerId = null;
let _seqRunning = false;
let _melodyState = { lastNote: 'A', lastOct: 0 };

function playPad(freq, time, dur) {
  if (_muted || _musicMuted) return;
  const c = ctx();
  // Triangle pad con slow attack y release
  const osc = c.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = freq;
  osc.detune.value = (Math.random() - 0.5) * 6;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, time);
  g.gain.exponentialRampToValueAtTime(0.06, time + 0.4);
  g.gain.setValueAtTime(0.06, time + dur - 0.5);
  g.gain.exponentialRampToValueAtTime(0.0001, time + dur);
  osc.connect(g);
  g.connect(_musicGain);
  osc.start(time);
  osc.stop(time + dur + 0.05);
}

function playBass(freq, time, dur = 0.35) {
  if (_muted || _musicMuted) return;
  const c = ctx();
  // Saw + lowpass = bass cálido
  const osc = c.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(freq, time);
  osc.frequency.exponentialRampToValueAtTime(freq * 0.99, time + dur);
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 600;
  filter.Q.value = 4;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, time);
  g.gain.exponentialRampToValueAtTime(0.18, time + 0.01);
  g.gain.exponentialRampToValueAtTime(0.001, time + dur);
  osc.connect(filter);
  filter.connect(g);
  g.connect(_musicGain);
  osc.start(time);
  osc.stop(time + dur + 0.05);
}

function playLead(freq, time, dur = 0.28) {
  if (_muted || _musicMuted) return;
  const c = ctx();
  const osc = c.createOscillator();
  osc.type = 'square';
  osc.frequency.value = freq;
  osc.detune.value = -3;
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 2400;
  filter.Q.value = 1;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, time);
  g.gain.exponentialRampToValueAtTime(0.07, time + 0.015);
  g.gain.exponentialRampToValueAtTime(0.001, time + dur);
  osc.connect(filter);
  filter.connect(g);
  g.connect(_musicGain);
  osc.start(time);
  osc.stop(time + dur + 0.05);
}

function playKick(time) {
  if (_muted || _musicMuted) return;
  const c = ctx();
  const osc = c.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(120, time);
  osc.frequency.exponentialRampToValueAtTime(40, time + 0.13);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, time);
  g.gain.exponentialRampToValueAtTime(0.32, time + 0.005);
  g.gain.exponentialRampToValueAtTime(0.001, time + 0.18);
  osc.connect(g);
  g.connect(_musicGain);
  osc.start(time);
  osc.stop(time + 0.2);
}

function playSnare(time) {
  if (_muted || _musicMuted) return;
  const c = ctx();
  const dur = 0.16;
  const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  const noise = c.createBufferSource();
  noise.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = 1500;
  const g = c.createGain();
  g.gain.setValueAtTime(0.16, time);
  g.gain.exponentialRampToValueAtTime(0.001, time + dur);
  noise.connect(filter);
  filter.connect(g);
  g.connect(_musicGain);
  noise.start(time);
  noise.stop(time + dur);
  // Click body
  const osc = c.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(220, time);
  osc.frequency.exponentialRampToValueAtTime(140, time + 0.06);
  const og = c.createGain();
  og.gain.setValueAtTime(0.10, time);
  og.gain.exponentialRampToValueAtTime(0.001, time + 0.07);
  osc.connect(og);
  og.connect(_musicGain);
  osc.start(time);
  osc.stop(time + 0.08);
}

function playHihat(time, open = false) {
  if (_muted || _musicMuted) return;
  const c = ctx();
  const dur = open ? 0.20 : 0.04;
  const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  const noise = c.createBufferSource();
  noise.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = 7000;
  const g = c.createGain();
  g.gain.setValueAtTime(open ? 0.06 : 0.05, time);
  g.gain.exponentialRampToValueAtTime(0.001, time + dur);
  noise.connect(filter);
  filter.connect(g);
  g.connect(_musicGain);
  noise.start(time);
  noise.stop(time + dur);
}

function scheduleStep(stepIdx, time) {
  const bar = Math.floor(stepIdx / STEPS_PER_BAR) % CHORD_PROG.length;
  const beat = stepIdx % STEPS_PER_BAR;
  const chord = CHORD_PROG[bar];

  // PAD: chord cada inicio de bar (4 barras de duración)
  if (beat === 0) {
    const padDur = SEC_PER_BEAT * 4;
    for (const n of chord.notes) playPad(freqOf(n, 0), time, padDur);
    // refuerzo octava arriba del root
    playPad(freqOf(chord.root, 1), time, padDur);
  }

  // BASS: root en step 0, 8, y un walk en 12
  if (beat === 0 || beat === 8) playBass(freqOf(chord.root, -1), time, 0.35);
  if (beat === 12) {
    // walk al siguiente chord root
    const nextChord = CHORD_PROG[(bar + 1) % CHORD_PROG.length];
    playBass(freqOf(nextChord.root, -1), time, 0.30);
  }

  // KICK: 0, 6, 8, 14 (boombap-ish)
  if (beat === 0 || beat === 8) playKick(time);
  if (beat === 6 || beat === 14) playKick(time);

  // SNARE: 4, 12 (backbeat)
  if (beat === 4 || beat === 12) playSnare(time);

  // HIHAT: cada step par
  if (beat % 2 === 0) playHihat(time, false);
  if (beat === 14) playHihat(time, true);  // open hat ocasional

  // LEAD: melodía aleatoria pentatonica en offbeats
  if (beat % 2 === 1 && Math.random() < 0.45) {
    const note = PENTATONIC_A_MIN[Math.floor(Math.random() * PENTATONIC_A_MIN.length)];
    // tend hacia la nota previa: 60% queda en misma octava, 25% sube, 15% baja
    let oct = _melodyState.lastOct;
    const r = Math.random();
    if (r < 0.25) oct = Math.min(2, oct + 1);
    else if (r < 0.40) oct = Math.max(-1, oct - 1);
    _melodyState.lastNote = note;
    _melodyState.lastOct = oct;
    playLead(freqOf(note, oct), time, 0.22 + Math.random() * 0.12);
  }
  // Pequeño fill al final de la progresión (último bar, últimos 4 steps)
  if (bar === CHORD_PROG.length - 1 && beat >= 12 && beat % 2 === 0) {
    const note = PENTATONIC_A_MIN[Math.floor(Math.random() * 3) + 2];  // notas altas
    playLead(freqOf(note, 1), time, 0.18);
  }
}

function seqLoop() {
  if (!_seqRunning) return;
  const c = ctx();
  // Schedule ahead 120ms
  while (_seqNextTime < c.currentTime + 0.12) {
    scheduleStep(_seqStep, _seqNextTime);
    _seqNextTime += SEC_PER_16TH;
    _seqStep++;
  }
  _seqTimerId = setTimeout(seqLoop, 25);
}

export function startMusic() {
  if (_seqRunning) return;
  const c = ctx();
  _seqRunning = true;
  _seqStep = 0;
  _seqNextTime = c.currentTime + 0.05;
  _melodyState = { lastNote: 'A', lastOct: 0 };
  seqLoop();
}
export function stopMusic() {
  _seqRunning = false;
  if (_seqTimerId) { clearTimeout(_seqTimerId); _seqTimerId = null; }
}
